import type {
  ColumnConfig,
  EmptyState,
  PaginationConfig,
  RowConfig,
  SortConfig,
  TableToolbarConfig,
} from '@/shared/types/table';
import { applyFilters } from './filterUtils';
import type { AdvancedFilterConfig } from '../types/filter';

/**
 * Búsqueda interna por columnas visibles (misma lógica que `Table.tsx`).
 */
export function searchRowsWithColumns<T>(params: {
  initialRows: RowConfig<T>[];
  columns: ColumnConfig<T>[];
  internalSearchQuery: string;
  internalSearchEnabled: boolean | undefined;
}): RowConfig<T>[] {
  const { initialRows, columns, internalSearchQuery, internalSearchEnabled } = params;

  if (!internalSearchQuery.trim()) return initialRows;

  const shouldApplyInternalSearch = internalSearchEnabled ?? false;
  if (!shouldApplyInternalSearch) {
    return initialRows;
  }

  const searchLower = internalSearchQuery.toLowerCase().trim();

  return initialRows.filter((row) => {
    return columns.some((column) => {
      if (column.visible === false) return false;

      let value: unknown;
      if (typeof column.accessor === 'function') {
        value = column.accessor(row);
      } else if (column.accessor) {
        value = row[column.accessor as keyof typeof row];
      }

      if (value !== null) {
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(searchLower);
      }

      return false;
    });
  });
}

export function filterRowsWithAdvancedFilters<T>(params: {
  searchedRows: RowConfig<T>[];
  filters: AdvancedFilterConfig[];
  columns: ColumnConfig<T>[];
  internalFiltersEnabled: boolean | undefined;
}): RowConfig<T>[] {
  const { searchedRows, filters, columns, internalFiltersEnabled } = params;

  if (filters.length === 0) return searchedRows;

  const shouldApplyInternalFilters = internalFiltersEnabled ?? false;
  if (!shouldApplyInternalFilters) {
    return searchedRows;
  }

  return applyFilters(
    searchedRows as RowConfig<unknown>[],
    filters,
    columns as ColumnConfig<unknown>[]
  ) as RowConfig<T>[];
}

export function sortRowsByColumn<T>(params: {
  filteredRows: RowConfig<T>[];
  sortConfig: SortConfig | null | undefined;
  columns: ColumnConfig<T>[];
}): RowConfig<T>[] {
  const { filteredRows, sortConfig, columns } = params;

  if (!sortConfig || !sortConfig.direction) return filteredRows;

  return [...filteredRows].sort((a, b) => {
    const column = columns.find((col) => col.id === sortConfig.columnId);
    if (!column) return 0;

    let aValue: unknown;
    let bValue: unknown;

    if (typeof column.accessor === 'function') {
      aValue = column.accessor(a);
      bValue = column.accessor(b);
    } else if (column.accessor) {
      aValue = a[column.accessor as keyof typeof a];
      bValue = b[column.accessor as keyof typeof b];
    } else {
      return 0;
    }

    const aNull = aValue === null || aValue === undefined;
    const bNull = bValue === null || bValue === undefined;
    if (aNull && bNull) return 0;
    if (aNull) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bNull) return sortConfig.direction === 'asc' ? -1 : 1;

    const aVal = aValue as number | string;
    const bVal = bValue as number | string;
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
}

export function paginateRowsForDisplay<T>(params: {
  sortedRows: RowConfig<T>[];
  paginationConfig: PaginationConfig | undefined;
  toolbarConfig: TableToolbarConfig | undefined;
  totalAfterFilters: number;
}): RowConfig<T>[] {
  const { sortedRows, paginationConfig, toolbarConfig, totalAfterFilters } = params;

  if (!paginationConfig?.enabled) {
    return sortedRows;
  }

  const currentPage = paginationConfig.currentPage || 1;
  const pageSize = paginationConfig.pageSize || 10;

  const isExternalSearch = !(toolbarConfig?.internalSearch ?? false);
  const isExternalFilters = !(toolbarConfig?.internalFilters ?? false);

  const hasExternalPagination =
    paginationConfig.total !== undefined && paginationConfig.total !== totalAfterFilters;

  if (hasExternalPagination || (isExternalSearch && isExternalFilters)) {
    return sortedRows;
  }

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  return sortedRows.slice(startIndex, endIndex);
}

export function resolveEffectiveEmptyState(params: {
  empty: EmptyState;
  rowsWithEditStateLength: number;
  internalSearchQuery: string;
  filtersLength: number;
  initialRowsLength: number;
  searchedRowsLength: number;
  filteredRowsLength: number;
}): EmptyState {
  const {
    empty,
    rowsWithEditStateLength,
    internalSearchQuery,
    filtersLength,
    initialRowsLength,
    searchedRowsLength,
    filteredRowsLength,
  } = params;

  if (rowsWithEditStateLength > 0) return empty;

  const hasSearchQuery = internalSearchQuery.trim().length > 0;
  const hasFilters = filtersLength > 0;

  if (initialRowsLength === 0 && !hasSearchQuery && !hasFilters) {
    return {
      ...empty,
      type: empty.type || ('empty' as const),
    };
  }

  if (hasSearchQuery && (searchedRowsLength === 0 || rowsWithEditStateLength === 0)) {
    return {
      type: 'no-results' as const,
      title: `No hay resultados para "${internalSearchQuery}"`,
      description: undefined,
      action: empty.action,
    };
  }

  if (hasFilters && filteredRowsLength === 0) {
    return {
      type: 'no-filters' as const,
      title: undefined,
      description: undefined,
      action: empty.action,
    };
  }

  return empty;
}
